import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Sparkles, Trash2 } from 'lucide-react';
import type { Connection } from './ConnectionModal';

type ChatMessage = { id: string; role: 'user' | 'assistant'; content: string };

const CONTEXT_OPTIONS: { id: string; label: string; description: string; stub?: boolean }[] = [
  {
    id: 'transcripts',
    label: 'All meeting transcripts',
    description: 'Transcripts linked to this contact (same source as the list on this page).',
  },
  {
    id: 'icp',
    label: 'Their ICP',
    description: 'Ideal customer profile field on this connection.',
  },
  {
    id: 'call_me_when',
    label: "Their call me when's",
    description: 'All three call-me-when statements from this profile.',
  },
  {
    id: 'related_emails',
    label: 'Emails that include this person',
    description: 'Messages or threads that reference this contact—no data source yet; here to preview what we could add.',
    stub: true,
  },
];

interface ConnectionAiResourceMockProps {
  isOpen: boolean;
  onClose: () => void;
  connection: Connection;
}

const ConnectionAiResourceMock: React.FC<ConnectionAiResourceMockProps> = ({
  isOpen,
  onClose,
  connection,
}) => {
  const [toggles, setToggles] = useState<Record<string, boolean>>(() => ({
    transcripts: true,
    icp: true,
    call_me_when: true,
    related_emails: false,
  }));
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState('');
  const [asking, setAsking] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, asking]);

  useEffect(() => {
    if (!isOpen) {
      setChatMessages([]);
      setPrompt('');
      setAsking(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const selectedLabels = CONTEXT_OPTIONS.filter((o) => toggles[o.id]).map((o) => o.label);

  const buildMockReply = useCallback(
    (userText: string) => {
      const ctxLines =
        selectedLabels.length > 0
          ? selectedLabels.map((l) => `• ${l}`).join('\n')
          : '• (no context blocks selected)';
      const who = connection.full_name?.trim() || 'This contact';
      return [
        '[UI preview only — no API call]',
        '',
        `${who} · ${connection.email_address || 'no email'}`,
        '',
        'With your current context selection, a real assistant would see something like:',
        ctxLines,
        '',
        `Your message: ${userText}`,
        '',
        'Later turns would reuse this context bundle plus the full thread — same pattern as transcript Q&A, but multi-source from the left panel.',
      ].join('\n');
    },
    [connection.email_address, connection.full_name, selectedLabels],
  );

  const clearChat = () => {
    setChatMessages([]);
  };

  const sendMock = () => {
    const p = prompt.trim();
    if (!p || asking) return;
    const uid = `u-${Date.now()}`;
    setChatMessages((prev) => [...prev, { id: uid, role: 'user', content: p }]);
    setPrompt('');
    setAsking(true);
    window.setTimeout(() => {
      setAsking(false);
      setChatMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'assistant', content: buildMockReply(p) },
      ]);
    }, 650);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 dark:bg-black/60"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative ml-auto flex h-full w-full max-w-[min(100vw,90rem)] flex-col bg-white shadow-xl dark:bg-gray-900 lg:flex-row"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connection-ai-resource-title"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-b border-gray-200 dark:border-gray-700 lg:border-b-0 lg:border-r">
          <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h2
                id="connection-ai-resource-title"
                className="truncate text-lg font-semibold text-gray-900 dark:text-white"
              >
                Resource assistant
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Preview · {connection.full_name}
              </p>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="mb-5 rounded-lg border border-violet-200 bg-violet-50/80 px-3 py-2.5 dark:border-violet-900/50 dark:bg-violet-950/30">
              <p className="text-xs font-medium text-violet-900 dark:text-violet-100">
                Mock only — layout and controls are real; nothing is sent to a model yet.
              </p>
            </div>

            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Context for each prompt
            </p>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Choose what would be bundled into the assistant&apos;s context window together with your message and
              prior turns (same idea as transcript chat + RAG, but multi-source).
            </p>

            <ul className="space-y-3">
              {CONTEXT_OPTIONS.map((opt) => (
                <li
                  key={opt.id}
                  className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-3 dark:border-gray-600 dark:bg-gray-800/40"
                >
                  <input
                    type="checkbox"
                    id={`ctx-${opt.id}`}
                    checked={Boolean(toggles[opt.id])}
                    onChange={() => setToggles((t) => ({ ...t, [opt.id]: !t[opt.id] }))}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-violet-600 focus:ring-violet-500 dark:border-gray-600 dark:bg-gray-900"
                  />
                  <label htmlFor={`ctx-${opt.id}`} className="min-w-0 cursor-pointer">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</span>
                      {opt.stub ? (
                        <span className="rounded px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-900 dark:bg-amber-900/45 dark:text-amber-100">
                          Stub
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{opt.description}</span>
                  </label>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-900/60">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Active context summary
              </p>
              {selectedLabels.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Nothing selected — prompts would use thread only.</p>
              ) : (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-800 dark:text-gray-200">
                  {selectedLabels.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <aside className="flex max-h-screen min-h-0 w-full shrink-0 flex-col border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-950 lg:max-h-none lg:w-[min(100%,32rem)] lg:border-l lg:border-t-0 xl:w-[36rem] 2xl:w-[40rem]">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-6 w-6 shrink-0 text-violet-600 dark:text-violet-400" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                Chat across resources
              </h3>
            </div>
            {chatMessages.length > 0 ? (
              <button
                type="button"
                onClick={clearChat}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800 disabled:opacity-50"
                title="Clear conversation"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear
              </button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {chatMessages.length === 0 ? (
              <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                Each send would attach your <strong>checked context</strong> from the left, plus this{' '}
                <strong>conversation so far</strong> — same flow as transcript chat. Here you can step through a
                multi-turn thread without leaving the contact.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {chatMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[95%] rounded-2xl px-4 py-3 text-base leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-violet-600 text-white'
                          : 'border border-gray-200 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100'
                      }`}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80 mb-1.5">
                        {m.role === 'user' ? 'You' : 'Assistant'}
                      </p>
                      <pre className="whitespace-pre-wrap break-words font-sans">{m.content}</pre>
                    </div>
                  </div>
                ))}
                {asking ? (
                  <div className="flex justify-start">
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400">
                      Thinking…
                    </div>
                  </div>
                ) : null}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-950">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400" htmlFor="connection-resource-prompt-input">
              Message
            </label>
            <textarea
              id="connection-resource-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (!asking && prompt.trim()) sendMock();
                }
              }}
              rows={3}
              placeholder="Ask a question… (⌘/Ctrl+Enter to send)"
              className="mt-2 min-h-[4.5rem] w-full resize-y rounded-lg border border-gray-300 bg-white px-3.5 py-3 text-base leading-relaxed text-gray-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-white disabled:opacity-50"
              disabled={asking}
            />
            <button
              type="button"
              onClick={sendMock}
              disabled={asking || !prompt.trim()}
              className="mt-3 w-full rounded-lg bg-violet-600 px-4 py-3 text-base font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {asking ? 'Sending…' : 'Send'}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ConnectionAiResourceMock;
